import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { trialAPI } from '../lib/api';
import { Button } from './ui/button';
import { AlertTriangle, Clock, XOctagon } from 'lucide-react';
import UpgradeLeadDialog from './UpgradeLeadDialog';
import PlanPickerDialog from './PlanPickerDialog';

/**
 * TrialBanner
 *
 * Shows the current organisation's trial status at the top of every
 * authenticated page. All derived values (is_trial, days_remaining,
 * is_expired) are read from the server (`GET /api/trial/status`) which
 * is tenant-scoped via `get_tenant_scope` — so an org can never see
 * another org's trial information.
 *
 * Visibility rules:
 *   - is_trial === true  → informational / warning banner
 *   - is_expired === true → red "trial ended" banner
 *   - active (paid) org  → banner is hidden
 */
export default function TrialBanner() {
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [planPickerOpen, setPlanPickerOpen] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const res = await trialAPI.getStatus();
      setStatus(res.data);
    } catch (e) {
      // Silent fail — banner just stays hidden on error (no auth leak).
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && user) {
      fetchStatus();
    } else if (!user) {
      // Clear status when logged out so a stale banner never leaks across sessions.
      setStatus(null);
    }
  }, [authLoading, user, fetchStatus]);

  // Listen for trial-reminder notification clicks → open the upgrade dialog.
  useEffect(() => {
    const open = () => setPlanPickerOpen(true);
    window.addEventListener('elara:open-upgrade-dialog', open);
    return () => window.removeEventListener('elara:open-upgrade-dialog', open);
  }, []);

  // Refresh when a payment just completed (dispatched by PaymentSuccess).
  useEffect(() => {
    const refresh = () => fetchStatus();
    window.addEventListener('elara:billing-updated', refresh);
    return () => window.removeEventListener('elara:billing-updated', refresh);
  }, [fetchStatus]);

  // Hidden when: no user, no status, status unknown, or org is active (paid).
  if (!user || !status) return null;
  if (status.status === 'unknown') return null;
  // Show for trial, expired, past_due. Hide for active + read_only.
  if (!status.is_trial && !status.is_expired && !status.is_past_due) return null;

  const daysRemaining = typeof status.days_remaining === 'number' ? status.days_remaining : null;
  const endsAt = status.trial_ends_at ? new Date(status.trial_ends_at) : null;
  const endsAtLabel = endsAt
    ? endsAt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : null;

  // Determine visual state
  let variant = 'info';
  if (status.is_expired || status.is_past_due) variant = 'expired';
  else if (daysRemaining !== null && daysRemaining <= 7) variant = 'warning';

  const styles = {
    info: {
      container: 'bg-[#1ABC9C]/10 border-[#1ABC9C]/40 text-[#1ABC9C]',
      icon: Clock,
      iconClass: 'text-[#1ABC9C]',
    },
    warning: {
      container: 'bg-amber-500/10 border-amber-500/40 text-amber-300',
      icon: AlertTriangle,
      iconClass: 'text-amber-400',
    },
    expired: {
      container: 'bg-red-500/10 border-red-500/40 text-red-300',
      icon: XOctagon,
      iconClass: 'text-red-400',
    },
  }[variant];

  const Icon = styles.icon;

  // Message
  let headline;
  if (status.is_past_due) {
    headline = 'Your last payment failed — please update billing to avoid service interruption.';
  } else if (status.is_expired) {
    headline = `Your trial ended${endsAtLabel ? ` on ${endsAtLabel}` : ''}.`;
  } else if (daysRemaining === 0) {
    headline = `Your trial ends today${endsAtLabel ? ` (${endsAtLabel})` : ''}.`;
  } else if (daysRemaining === 1) {
    headline = `1 day remaining in your trial${endsAtLabel ? ` — ends ${endsAtLabel}` : ''}.`;
  } else if (daysRemaining !== null) {
    headline = `${daysRemaining} days remaining in your trial${endsAtLabel ? ` — ends ${endsAtLabel}` : ''}.`;
  } else {
    headline = 'Trial active.';
  }

  // Secondary copy
  const secondary = status.is_expired
    ? 'Upgrade now to restore full access to your workspace.'
    : variant === 'warning'
    ? 'Upgrade soon to keep your team working without interruption.'
    : 'Enjoy full access while you evaluate Elara.';

  return (
    <div
      className={`w-full border-b px-4 py-2 md:px-6 ${styles.container}`}
      role={status.is_expired ? 'alert' : 'status'}
      data-testid="trial-banner"
      data-trial-variant={variant}
    >
      <div className="flex items-center gap-3 max-w-screen-2xl mx-auto">
        <Icon className={`w-5 h-5 shrink-0 ${styles.iconClass}`} />
        <div className="flex-1 min-w-0">
          <p
            className="text-sm font-medium truncate"
            data-testid="trial-banner-headline"
          >
            {headline}
          </p>
          <p className="text-xs opacity-80 hidden sm:block" data-testid="trial-banner-secondary">
            {secondary}
          </p>
        </div>
        {/*
          Upgrade CTA — admins only. Opens the tiered plan picker; Enterprise+
          falls through to the lead-capture flow (contact sales).
        */}
        {user?.role === 'admin' && (
          <Button
            size="sm"
            variant="outline"
            className="shrink-0 border-current text-current hover:bg-white/10"
            onClick={() => setPlanPickerOpen(true)}
            data-testid="trial-banner-upgrade-btn"
          >
            {status.is_expired ? 'Upgrade' : 'Upgrade plan'}
          </Button>
        )}
      </div>
      <PlanPickerDialog
        open={planPickerOpen}
        onOpenChange={setPlanPickerOpen}
        onContactSales={() => setLeadOpen(true)}
      />
      <UpgradeLeadDialog
        open={leadOpen}
        onOpenChange={setLeadOpen}
        defaultOrgName={status.organisation_name || ''}
        defaultName={user?.name || ''}
        defaultEmail={user?.email || ''}
      />
    </div>
  );
}
